/** The booking grid, shared by the slot strip and the time picker.
 *
 *  Reported 2026-10-18: the picker moved a minute at a time while the
 *  strip under it showed 30-minute slots, so the two disagreed about
 *  what a valid time was and 11:31 could be booked.
 */
import { useQuery } from "@tanstack/react-query";
import type dayjs from "dayjs";

import type { SlotsResponse } from "@/components/SlotAvailability";
import { ApiPaths, api } from "@/lib/api";
import { getDisplayTimeZone } from "@/lib/format";

/** YYYY-MM-DD for the day the picker is on, in the ORG's zone.
 *
 *  Not UTC — toISOString() would roll a late-evening slot into tomorrow.
 *  And not the browser's zone either: the server matches slots on the
 *  org's local day, so an operator whose laptop is set to another zone
 *  would ask for one day and be shown another. Falls back to the browser
 *  when no org zone is set, which is what the rest of the app does. */
export function localDay(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // en-CA formats as YYYY-MM-DD, which is exactly the wire format.
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(getDisplayTimeZone() ? { timeZone: getDisplayTimeZone() } : {}),
  }).format(d);
}

/** Round a picked time to the nearest grid start. */
export function snapToGrid(
  d: dayjs.Dayjs,
  step: number,
  offset = 0,
): dayjs.Dayjs {
  if (step <= 1) return d.second(0).millisecond(0);
  const minutesOfDay = d.hour() * 60 + d.minute();
  const snapped =
    Math.round((minutesOfDay - offset) / step) * step + offset;
  return d.startOf("day").add(snapped, "minute");
}

/** The org's booking grid, for `<MuiDateTimePicker minutesStep>`.
 *
 *  Asked once (the grid is per org, not per day) and cached for the
 *  session. Until it arrives the picker keeps minute precision; the
 *  server refuses an off-grid time either way. */
export function useSlotGrid(): { minutesStep?: number; offsetMinutes?: number } {
  const today = localDay(new Date().toISOString());
  const q = useQuery({
    queryKey: ["scheduling-grid"],
    enabled: !!today,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const res = await api.get<SlotsResponse>(
        `${ApiPaths.schedulingAvailability}?date=${today as string}`,
      );
      return res.data;
    },
  });
  return {
    minutesStep: q.data?.slot_minutes,
    offsetMinutes: q.data?.offset_minutes,
  };
}
